"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebVitalsInstrumentation = void 0;
const attribution_1 = require("web-vitals/attribution");
const instrumentation_1 = require("@opentelemetry/instrumentation");
const version_1 = require("./version");
/**
 * Web vitals auto-instrumentation, sends spans automatically for CLS, LCP, INP, FCP, FID, TTFB.
 * Defaults to sending spans for CLS, LCP, INP, FCP and TTFB.
 * @param config The {@link WebVitalsInstrumentationConfig }
 */
class WebVitalsInstrumentation extends instrumentation_1.InstrumentationBase {
    constructor(config = {
        enabled: true,
    }) {
        super('@honeycombio/instrumentation-web-vitals', version_1.VERSION, {
            /**
             * NOTE: this is an unfortunate necessity to initially set
             * the enabled state of the instrumentation to false because
             * super gets called before anything else and in the parent class
             * if enabled is true, it will call `this.enable()` and the `enable`
             * function will run before any of the config options (e.g. vitalsToTrack)
             * can become available. So we're setting this explicitly to false, making config options available,
             * and then enabling the instrumentation in this constructor.
             *
             * This is usually not an issue when instrumentation is patching functions and not calling them
             * directly, this instrumentation is a bit of a special case.
             **/
            enabled: false,
        });
        this.onReportCLS = (cls, applyCustomAttributes) => {
            const { name, attribution } = cls;
            const { largestShiftTarget, largestShiftTime, largestShiftValue, loadState, largestShiftEntry, } = attribution;
            const attrPrefix = this.getAttrPrefix(name);
            const span = this.tracer.startSpan(name);
            span.setAttributes(Object.assign(Object.assign({}, this.getSharedAttributes(cls)), { [`${attrPrefix}.largest_shift_target`]: largestShiftTarget, [`${attrPrefix}.element`]: largestShiftTarget, [`${attrPrefix}.largest_shift_time`]: largestShiftTime, [`${attrPrefix}.largest_shift_value`]: largestShiftValue, [`${attrPrefix}.load_state`]: loadState, [`${attrPrefix}.had_recent_input`]: largestShiftEntry === null || largestShiftEntry === void 0 ? void 0 : largestShiftEntry.hadRecentInput }));
            if (applyCustomAttributes) {
                applyCustomAttributes(cls, span);
            }
            span.end();
        };
        this.onReportLCP = (lcp, applyCustomAttributes) => {
            const { name, attribution } = lcp;
            const { element, url, timeToFirstByte, resourceLoadDelay, resourceLoadTime, elementRenderDelay, } = attribution;
            const attrPrefix = this.getAttrPrefix(name);
            const span = this.tracer.startSpan(name);
            span.setAttributes(Object.assign(Object.assign({}, this.getSharedAttributes(lcp)), { [`${attrPrefix}.element`]: element, [`${attrPrefix}.url`]: url, [`${attrPrefix}.time_to_first_byte`]: timeToFirstByte, [`${attrPrefix}.resource_load_delay`]: resourceLoadDelay, [`${attrPrefix}.resource_load_time`]: resourceLoadTime, [`${attrPrefix}.element_render_delay`]: elementRenderDelay }));
            if (applyCustomAttributes) {
                applyCustomAttributes(lcp, span);
            }
            span.end();
        };
        this.onReportINP = (inp, applyCustomAttributes) => {
            const { name, attribution } = inp;
            const { eventTarget, eventType, loadState } = attribution;
            const attrPrefix = this.getAttrPrefix(name);
            const span = this.tracer.startSpan(name);
            span.setAttributes(Object.assign(Object.assign({}, this.getSharedAttributes(inp)), { [`${attrPrefix}.element`]: eventTarget, [`${attrPrefix}.event_type`]: eventType, [`${attrPrefix}.load_state`]: loadState }));
            if (applyCustomAttributes) {
                applyCustomAttributes(inp, span);
            }
            span.end();
        };
        this.onReportFCP = (fcp, applyCustomAttributes) => {
            const { name, attribution } = fcp;
            const { timeToFirstByte, firstByteToFCP, loadState } = attribution;
            const attrPrefix = this.getAttrPrefix(name);
            const span = this.tracer.startSpan(name);
            span.setAttributes(Object.assign(Object.assign({}, this.getSharedAttributes(fcp)), { [`${attrPrefix}.time_to_first_byte`]: timeToFirstByte, [`${attrPrefix}.time_since_first_byte`]: firstByteToFCP, [`${attrPrefix}.load_state`]: loadState }));
            if (applyCustomAttributes) {
                applyCustomAttributes(fcp, span);
            }
            span.end();
        };
        this.onReportFID = (fid, applyCustomAttributes) => {
            const { name, attribution } = fid;
            const { eventTarget, eventType, loadState } = attribution;
            const attrPrefix = this.getAttrPrefix(name);
            const span = this.tracer.startSpan(name);
            span.setAttributes(Object.assign(Object.assign({}, this.getSharedAttributes(fid)), { [`${attrPrefix}.element`]: eventTarget, [`${attrPrefix}.event_type`]: eventType, [`${attrPrefix}.load_state`]: loadState }));
            if (applyCustomAttributes) {
                applyCustomAttributes(fid, span);
            }
            span.end();
        };
        this.onReportTTFB = (ttfb, applyCustomAttributes) => {
            const { name, attribution } = ttfb;
            const { waitingTime, dnsTime, connectionTime, requestTime, } = attribution;
            const attrPrefix = this.getAttrPrefix(name);
            const span = this.tracer.startSpan(name);
            span.setAttributes(Object.assign(Object.assign({}, this.getSharedAttributes(ttfb)), { [`${attrPrefix}.waiting_time`]: waitingTime, [`${attrPrefix}.dns_time`]: dnsTime, [`${attrPrefix}.connection_time`]: connectionTime, [`${attrPrefix}.request_time`]: requestTime }));
            if (applyCustomAttributes) {
                applyCustomAttributes(ttfb, span);
            }
            span.end();
        };
        this.vitalsToTrack = (config === null || config === void 0 ? void 0 : config.vitalsToTrack) || [
            'CLS',
            'LCP',
            'INP',
            'FCP',
            'TTFB',
        ];
        this.lcpOpts = config === null || config === void 0 ? void 0 : config.lcp;
        this.clsOpts = config === null || config === void 0 ? void 0 : config.cls;
        this.inpOpts = config === null || config === void 0 ? void 0 : config.inp;
        this.fidOpts = config === null || config === void 0 ? void 0 : config.fid;
        this.fcpOpts = config === null || config === void 0 ? void 0 : config.fcp;
        this.ttfbOpts = config === null || config === void 0 ? void 0 : config.ttfb;
        this.enabled = config === null || config === void 0 ? void 0 : config.enabled;
    }
    init() { }
    getAttrPrefix(name) {
        return name.toLowerCase();
    }
    getSharedAttributes(vital) {
        const { name, id, delta, rating, value, navigationType } = vital;
        const attrPrefix = this.getAttrPrefix(name);
        return {
            [`${attrPrefix}.id`]: id,
            [`${attrPrefix}.delta`]: delta,
            [`${attrPrefix}.value`]: value,
            [`${attrPrefix}.rating`]: rating,
            [`${attrPrefix}.navigation_type`]: navigationType,
        };
    }
    enable() {
        if (!this.enabled) {
            this._diag.debug(`Instrumentation disabled`);
            return;
        }
        this._diag.debug(`Sending spans for ${this.vitalsToTrack.join(',')}`);
        if (this.vitalsToTrack.includes('CLS')) {
            (0, attribution_1.onCLS)((vital) => {
                var _a;
                this.onReportCLS(vital, (_a = this.clsOpts) === null || _a === void 0 ? void 0 : _a.applyCustomAttributes);
            }, this.clsOpts);
        }
        if (this.vitalsToTrack.includes('LCP')) {
            (0, attribution_1.onLCP)((vital) => {
                var _a;
                this.onReportLCP(vital, (_a = this.lcpOpts) === null || _a === void 0 ? void 0 : _a.applyCustomAttributes);
            }, this.lcpOpts);
        }
        if (this.vitalsToTrack.includes('INP')) {
            (0, attribution_1.onINP)((vital) => {
                var _a;
                this.onReportINP(vital, (_a = this.inpOpts) === null || _a === void 0 ? void 0 : _a.applyCustomAttributes);
            }, this.inpOpts);
        }
        if (this.vitalsToTrack.includes('FID')) {
            (0, attribution_1.onFID)((vital) => {
                var _a;
                this.onReportFID(vital, (_a = this.fidOpts) === null || _a === void 0 ? void 0 : _a.applyCustomAttributes);
            }, this.fidOpts);
        }
        if (this.vitalsToTrack.includes('TTFB')) {
            (0, attribution_1.onTTFB)((vital) => {
                var _a;
                this.onReportTTFB(vital, (_a = this.ttfbOpts) === null || _a === void 0 ? void 0 : _a.applyCustomAttributes);
            }, this.ttfbOpts);
        }
        if (this.vitalsToTrack.includes('FCP')) {
            (0, attribution_1.onFCP)((vital) => {
                var _a;
                this.onReportFCP(vital, (_a = this.fcpOpts) === null || _a === void 0 ? void 0 : _a.applyCustomAttributes);
            }, this.fcpOpts);
        }
    }
}
exports.WebVitalsInstrumentation = WebVitalsInstrumentation;
